import { createElement, useState, type ComponentType, type CSSProperties } from 'react'
import {
  FaCheck,
  FaCommentDots,
  FaCopy,
  FaFacebook,
  FaHeart,
  FaInstagram,
  FaLinkedin,
  FaShareNodes,
  FaTiktok,
  FaUser,
  FaXTwitter,
  FaYoutube,
} from 'react-icons/fa6'
import './social-medias.page.css'
import logo from '../../assets/images/logo.png'

type IconComponent = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>

type SocialPlatform = {
  id: string
  name: string
  handle: string
  href: string
  color: string
  iconBg: string
  Icon: IconComponent
}

type ShareMessage = {
  id: string
  title: string
  accent: string
  Icon: IconComponent
  body: string
  hashtags: string
}

const PLATFORMS: SocialPlatform[] = [
  {
    id: 'instagram',
    name: 'Instagram',
    handle: '@transpotsapp',
    href: 'https://www.instagram.com/transpotsapp',
    color: '#E4405F',
    iconBg: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
    Icon: FaInstagram as IconComponent,
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    handle: '@TranspotsApp',
    href: 'https://www.linkedin.com/company/transpotsapp/',
    color: '#0A66C2',
    iconBg: '#0A66C2',
    Icon: FaLinkedin as IconComponent,
  },
  {
    id: 'youtube',
    name: 'YouTube',
    handle: '@TranspotsApp',
    href: 'https://www.youtube.com/@TranspotsApp',
    color: '#FF0000',
    iconBg: '#FF0000',
    Icon: FaYoutube as IconComponent,
  },
  {
    id: 'facebook',
    name: 'Facebook',
    handle: '@TranspotsApp',
    href: 'https://www.facebook.com/TranspotsApp',
    color: '#1877F2',
    iconBg: '#1877F2',
    Icon: FaFacebook as IconComponent,
  },
  {
    id: 'x',
    name: 'X (Twitter)',
    handle: '@TranspotsApp',
    href: 'https://x.com/TranspotsApp',
    color: '#0F1419',
    iconBg: '#0F1419',
    Icon: FaXTwitter as IconComponent,
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    handle: '@transpotsapp',
    href: 'https://www.tiktok.com/@transpotsappcanada',
    color: '#010101',
    iconBg: '#010101',
    Icon: FaTiktok as IconComponent,
  },
]

const SHARE_MESSAGES: ShareMessage[] = [
  {
    id: 'short',
    title: 'Short',
    accent: '#0a57a8',
    Icon: FaCommentDots as IconComponent,
    body:
      'Great to attend the Transpots Product Launch and see how AI can help simplify trucking operations.\n\nIf you work in trucking or know someone who could benefit from a simpler way to manage dispatch, drivers, trips, documents, and invoicing reach out to Transpots for a demo.',
    hashtags: '#Transpots #AIForTrucking #Trucking #TransportationTechnology',
  },
  {
    id: 'personal',
    title: 'Personal',
    accent: '#16a34a',
    Icon: FaUser as IconComponent,
    body:
      "Had a great time at the Transpots Product Launch!\n\nIt was exciting to see how Transpots is using AI and automation to simplify everyday trucking operations from dispatch and driver communication to PODs and invoicing.\n\nIf you're in the trucking industry, or know a trucking company that could benefit from Transpots, connect with the team and request a demo.",
    hashtags: '#Transpots #AIForTrucking #TruckingTechnology #Logistics',
  },
  {
    id: 'supportive',
    title: 'Supportive',
    accent: '#7c3aed',
    Icon: FaHeart as IconComponent,
    body:
      'Congratulations to the Transpots team on a successful product launch!\n\nGreat to see a Canadian startup building practical AI technology for the trucking industry and working to make everyday operations simpler for carriers.\n\nIf you run a trucking company or know someone who could benefit from Transpots reach out to the team and book a demo.',
    hashtags: '#Transpots #AIForTrucking #Trucking #Logistics #CanadianStartup',
  },
]

const SocialMediasPage = () => {
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const handleCopy = async (message: ShareMessage) => {
    const text = `${message.body}\n\n${message.hashtags}`
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(message.id)
      window.setTimeout(() => setCopiedId((current) => (current === message.id ? null : current)), 2000)
    } catch {
      // Fallback for older browsers / insecure contexts
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedId(message.id)
      window.setTimeout(() => setCopiedId((current) => (current === message.id ? null : current)), 2000)
    }
  }

  return (
    <main className="social-medias-page">
      <div className="social-medias-page__inner">
        <header className="social-medias-page__header">
          <img className="social-medias-page__logo" src={logo} alt="Transpots" />
          <h1 className="social-medias-page__title">Follow Transpots</h1>
          <p className="social-medias-page__subtitle">
            Join our community on social media and stay connected with the latest news, tips
            and updates.
          </p>
        </header>

        <div className="social-medias-page__grid">
          {PLATFORMS.map(({ Icon, ...platform }) => (
            <a
              key={platform.id}
              className="social-medias-card"
              href={platform.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${platform.name} — ${platform.handle}`}
            >
              <div
                className="social-medias-card__icon"
                style={
                  {
                    background: platform.iconBg,
                    '--icon-glow': platform.color,
                  } as CSSProperties
                }
              >
                {createElement(Icon)}
              </div>
              <div className="social-medias-card__text">
                <h2 className="social-medias-card__name">{platform.name}</h2>
                <p className="social-medias-card__handle">{platform.handle}</p>
              </div>
            </a>
          ))}
        </div>

        <section className="share-experience" aria-labelledby="share-experience-title">
          <div className="share-experience__header">
            <div className="share-experience__badge" aria-hidden="true">
              {createElement(FaShareNodes as IconComponent)}
            </div>
            <div>
              <h2 id="share-experience-title" className="share-experience__title">
                Share Your Experience
              </h2>
              <p className="share-experience__intro">
                Help us spread the word about Transpots. Choose a message below, personalize it
                if you&apos;d like, add a photo from the event, and share it with your network.
              </p>
            </div>
          </div>

          <div className="share-experience__grid">
            {SHARE_MESSAGES.map((message) => {
              const isCopied = copiedId === message.id
              return (
                <article
                  key={message.id}
                  className="share-message-card"
                  style={{ '--share-accent': message.accent } as CSSProperties}
                >
                  <div className="share-message-card__top">
                    <div className="share-message-card__label">
                      {createElement(message.Icon, {
                        className: 'share-message-card__label-icon',
                        'aria-hidden': true,
                      })}
                      <span>{message.title}</span>
                    </div>
                    <button
                      type="button"
                      className="share-message-card__copy"
                      onClick={() => handleCopy(message)}
                      aria-label={`Copy ${message.title} message`}
                    >
                      {createElement((isCopied ? FaCheck : FaCopy) as IconComponent, {
                        'aria-hidden': true,
                      })}
                      <span>{isCopied ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                  <p className="share-message-card__body">{message.body}</p>
                  <p className="share-message-card__hashtags">{message.hashtags}</p>
                </article>
              )
            })}
          </div>
        </section>
      </div>
    </main>
  )
}

export default SocialMediasPage
